import * as React from "react";
import { useSearchParams } from "react-router";
import TopBarProgress from "react-topbar-progress-indicator";
import * as Sentry from "@sentry/react";

import { Loader } from "@/components/ui/loader";
import { ErrorAlert } from "@/components/ui/errorAlert";
import { SharepointRow } from "@/modules/sharepoint/components/SharepointRow";
import { SharepointParentDir } from "@/modules/sharepoint/components/SharepointParentDir";
import { useGetSharepointItemsQuery } from "@/modules/sharepoint/sharepoint.api";
import * as analytics from "@/services/analytics";

export const SharepointView: React.FC = () => {
  const [searchParams] = useSearchParams();

  const mode = searchParams.get("mode") || "menu";
  const siteId = searchParams.get("site_id") || undefined;
  const driveId = searchParams.get("drive_id") || undefined;
  const folderId = searchParams.get("folder_id") || undefined;

  const { data, isLoading, isFetching, isError, error } = useGetSharepointItemsQuery({
    mode,
    site_id: siteId,
    drive_id: driveId,
    folder_id: folderId,
  });

  React.useEffect(() => {
    if (!isLoading && data?.status === "login_required") {
      analytics.sharepointLogin();
      document.location.href = "/pf-auth/oauth/o365-login";
    }
  }, [isLoading, data]);

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    Sentry.captureException(error);

    return <ErrorAlert title="Unable to load Sharepoint" error={error} />;
  }

  const showBreadcrumbs = mode !== "menu" && data?.navigation?.breadcrumbs && data.navigation.breadcrumbs.length > 1;

  return (
    <div>
      {isFetching && <TopBarProgress />}

      <div className="mb-6 pb-4">
        <div className="mb-2 flex flex-col justify-between space-y-2 sm:flex-row sm:space-y-0">
          <h1 className="text-2xl font-bold">Sharepoint</h1>
        </div>
      </div>

      <div className="divide-y divide-gray-200 rounded-lg bg-white">
        {showBreadcrumbs && <SharepointParentDir breadcrumbs={data.navigation.breadcrumbs} />}
        {!isFetching && data?.items.map(item => <SharepointRow key={item.id} item={item} />)}
      </div>
    </div>
  );
};
